import {Avatar, Button, Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';
import {UserFieldsFragment, useUsersQuery} from 'api/generated';
import {TableCard} from 'components';
import {UserStatusTag} from 'components/UserStatusTag';
import deepEqual from 'deep-equal';
import gravatar from 'gravatar';
import {useAsyncAction} from 'hooks/useAsyncAction';
// @ts-ignore
import initials from 'initials';
import {colors} from 'lib/colors';
import {omitEmpty} from 'lib/omitEmpty';
import moment from 'moment';
import React from 'react';
import {RouteComponentProps} from 'react-router-dom';
import styled from 'styled-components';
import {StringParam, useQueryParams} from 'use-query-params';
import {FilterQueryParams, UserFilters} from './UserFilters';

const LoadMore = styled.div`
  text-align: center;
`;

export const queryFiltersSchema = {
  email: StringParam,
  phone: StringParam,
  username: StringParam,
  status: StringParam
};

const builtFilter = ({email, phone, username, status}: {[key: string]: any}) => {
  if (email) {
    return `email^="${email}"`;
  }
  if (phone) {
    return `phone_number^="${phone}"`;
  }
  if (username) {
    return `username="${username}"`;
  }
  if (status) {
    return `cognito:user_status="${status}"`;
  }
  return undefined;
};

export const Users: React.FC<RouteComponentProps<{}>> = ({history}) => {
  const [queryFilters, setQueryFilters] = useQueryParams(queryFiltersSchema);
  const filter = builtFilter(queryFilters);
  const {data, loading, fetchMore, refetch} = useUsersQuery({variables: {filter}});
  const users = data?.users.items ?? [];
  const token = data?.users.nextToken;

  const [handleLoadMore, isLoadingMore] = useAsyncAction(() =>
    fetchMore({
      variables: {
        filter,
        token
      },
      updateQuery: (prev, {fetchMoreResult}) => {
        if (!fetchMoreResult) return prev;
        return Object.assign({}, prev, {
          users: {
            ...fetchMoreResult.users,
            items: [...prev.users.items, ...fetchMoreResult.users.items]
          }
        });
      }
    })
  );

  const columns: Array<ColumnProps<UserFieldsFragment>> = [
    {
      dataIndex: 'email',
      key: 'avatar',
      className: 'minimized-cell',
      render(email) {
        return (
          <Avatar
            src={gravatar.url(email, {default: '404'})}
            style={{backgroundColor: colors.primary}}>
            {initials(email)}
          </Avatar>
        );
      }
    },
    {
      title: 'Email',
      dataIndex: 'email'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      className: 'minimized-cell',
      render(status, user) {
        return <UserStatusTag user={user} />;
      }
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      className: 'minimized-cell',
      render(date) {
        return moment(date).format('lll');
      }
    }
  ];

  const handleFiltersChange = (values: FilterQueryParams) => {
    if (deepEqual(omitEmpty(values), omitEmpty(queryFilters))) {
      return refetch();
    }
    setQueryFilters(values, 'push');
  };

  const renderFooter = () => {
    if (!token) return null;
    return (
      <LoadMore>
        <Button onClick={handleLoadMore} loading={isLoadingMore}>
          Load more
        </Button>
      </LoadMore>
    );
  };

  return (
    <>
      <UserFilters onChange={handleFiltersChange} queryFilters={queryFilters} />
      <TableCard>
        <Table
          dataSource={users}
          columns={columns}
          loading={loading}
          rowClassName={() => 'clickable'}
          rowKey="id"
          onRow={(user) => ({
            onClick: (e) => {
              e.preventDefault();
              history.push(`/users/${user.id}`);
            }
          })}
          pagination={false}
          footer={token ? renderFooter : undefined}
        />
      </TableCard>
    </>
  );
};
